import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@mpt-fo/common/core/services/authentication.service';
import { AuthorizedUser } from '@mpt-fo/common/core/services/authorized-user.service';
import { ComplianceService } from '@mpt-fo/common/core/services/compliance.service';
import { DataLayerService } from '@mpt-fo/common/core/services/data-layer-service.service';
import { DeviceHelperService } from '@mpt-fo/common/core/services/device-helper.service';
import { DeviceService } from '@mpt-fo/common/core/services/device.service';
import { KycService } from '@mpt-fo/common/core/services/kyc-service';
import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import { LoginHelperService } from '@mpt-fo/common/core/services/login-helper.service';
import { MobileDelegateService } from '@mpt-fo/common/core/services/mobile-delegate.service';
import { MobileWebViewService } from '@mpt-fo/common/core/services/mobile-web-view.service';
import { ModalHelperService, ModalOptions } from '@mpt-fo/common/core/services/modal-helper.service';
import { ScreenManagerService } from '@mpt-fo/common/core/services/screen-manager.service';
import { ModalOptionsConfig, ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import { ErrorCode, LoginV2FormStep } from '@app/channels/onboarding/onboarding-v2/model/onboarding-v2-model';
import { AppConfig } from '@mpt-fo/common/shared/config/app-config';
import { ApplicationRoles, UserAlias } from '@mpt-fo/common/shared/constants';
import { LoginRequestModel, LoginV2RequestModel, OtpLoginResponse } from '@mpt-fo/common/shared/models/login.model';
import { ApplicationRole } from '@mpt-fo/common/shared/models/role.model';
import { MoniepointAppParameters } from '@mpt-fo/common/core/models/login.model';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { MessageType } from '@mpt-fo/common/shared/models/utility.model';
import { MessageModalComponent } from '../../message-modal/message-modal.component';
import { DeviceInformation } from '@mpt-fo/common/shared/models/device.model';
import { Datalayer, EventName, initializeDatalayer, updateDatalayerIdentifyUser } from '@mpt-fo/common/shared/models/data.layer.model';
import { NzTabType } from 'ng-zorro-antd/tabs';
import { PhoneNumberInput } from '@mpt-fo/common/kamona/components/kamona-input-field/kamona-input-field.component';
import {ProfileSetupService} from "@mpt-fo/common/core/services/profile-setup/profile-setup.service";
import { AccountsClosedModalComponent } from '@mpt-fo/common/lean-shared/components/accounts-closed-modal/accounts-closed-modal.component';
import { LocationHelper } from '@mpt-fo/common/core/helpers/location.helper';
import { ClusterManagerManagementService } from '@mpt-fo/common/core/services/cluster-manager-management/cluster-manager-management.service';

@Component({
	selector: 'app-web-login-v2',
	templateUrl: './web-login-v2.component.html',
	styleUrls: ['./web-login-v2.component.scss'],
})
export class WebLoginV2Component implements OnInit {
	userNameForm!: FormGroup;
	userPhoneNumberForm: FormGroup;
	passwordVisible = false;
	submitting: boolean;
	userRole;
	roles: ApplicationRole[] = ApplicationRoles;
	token: any;
	userType: any;
	currentView: any;
	otpResponse: any;
	timer: number;
	countDownTimer;
	countDown = false;
	otp: any;
	tokenData: OtpLoginResponse;
	otpForm: FormGroup;
	ussdCode: any;
	identifier: any;
	consolidationExpiryDate: any;
	hasClaimRequest: boolean;
	hasDuplicatedProfile: boolean;
	businesses: any[] = [];
	runningModal: any;
	readonly ROUTE = '/login/web';
	suspensionReleaseTime: any;
	isInitiated = false;
	readonly NETWORK_ERROR = 'Please make sure you have internet connectivity';
	webBicon;
	tabType: NzTabType = 'line';
	selectedTabIndex = 0;
	phoneNumber: PhoneNumberInput;
	readonly MAX_PHONE_LENGTH = 11;
	readonly MAX_PASSCODE_LENGTH = 6;
	loginStep = LoginV2FormStep;
	currentStep = LoginV2FormStep.LOGIN;

	constructor(
		public fb: FormBuilder,
		public router: Router,
		public config: AppConfig,
		public auth: AuthenticationService,
		public localStorage: LocalStorageService,
		public user: AuthorizedUser,
		public deviceService: DeviceService,
		public complianceService: ComplianceService,
		public modal: ModalService,
		public activatedRoute: ActivatedRoute,
		public mobileService: MobileWebViewService,
		public delegateService: MobileDelegateService,
		public kycService: KycService,
		public loginHelper: LoginHelperService,
		public modalHelper: ModalHelperService,
		public deviceHelper: DeviceHelperService,
		public modalService: ModalService,
		public dataLayerHelper: DataLayerService,
		public screenManager: ScreenManagerService,
		public profileSetupService: ProfileSetupService,
		public locationHelper: LocationHelper,
		private clusterManagerService: ClusterManagerManagementService,
	) {
	}

	get showLicensedAndInsuredInfo() {
		return this.config.SHOW_LICENSED_AND_SECURED_INFO;
	}

	ngOnInit(): void {
		this.initializeLogin();
	}

	initializeLogin() {
		this.webBicon = this.config.WEB_BICON;
		this.setForms();
		this.handleScreenMangerChange();
		this.handleLocationAccessCheck();
	}

	handleScreenMangerChange() {
		this.screenManager.isMobile$.subscribe((isMobile) => {
			const isContainWebLogin = this.router?.url.includes(this.ROUTE);
			const canRoute = isContainWebLogin && isMobile;
			if (canRoute) {
				this.getLoginParameters();
			}
		});
	}

	async handleLocationAccessCheck() {
		const hasPermission = await this.locationHelper.hasPermission();
		const isLocationAccessMandatory = this.config.ENABLE_MANDATORY_LOGIN_LOCATION_ACCESS;
	
		if(!hasPermission && isLocationAccessMandatory) {
			this.router.navigateByUrl('login/location-request-access');
		}
	}

	getLoginParameters() {
		const loginParameters: MoniepointAppParameters = this.mobileService.getMoniepointAppParameters();
		this.mobileService.saveMoniepointAppParameters(loginParameters);
		const username = loginParameters?.deviceRegistrationRequest?.username;
		const isSlashScreen = StringHelper.isNullOrEmptyOrUndefined(username) && this.mobileService.isMobileWebViewUser();
		const route = isSlashScreen ? 'mobile-v2/splash-screen' : 'mobile';
		this.router.navigateByUrl(`login/${route}`);
	}

	async handleOncloseMessageModal(param) {
		await this.handleOncloseMessageModalIfMessageTypeComplete(param?.messageType, param?.res);
		await this.handleOncloseMessageModalIfMessageTypeWarning(param?.messageType);
		if (this.runningModal) {
			this.runningModal.destroy();
		}
	}

	async handleOncloseMessageModalIfMessageTypeWarning(messageType) {
		if (messageType !== MessageType.WARNING) {
			return;
		}
		this.loginHelper.setLoginOtpResponse(this.otpResponse);
		await this.router.navigateByUrl('/login/web/device-registration');
	}

	async handleOncloseMessageModalIfMessageTypeComplete(messageType, res) {
		if (messageType !== MessageType.COMPLETE) {
			return;
		}
		const deviceInformation = this.getDeviceInformation();
		await this.loginHelper.processSessionUser(res, deviceInformation);
	}


	createModalConfig(params) {
		const config = new ModalOptionsConfig();
		config.nzWidth = 396;
		config.nzComponentParams.data = params;
		config.nzComponentParams.callback = async (result) => {
			await this.handleOncloseMessageModal(result);
		};
		return config;
	}

	launchMessageModal(params) {
		const config = this.createModalConfig(params);
		this.runningModal = this.modal.createModal(MessageModalComponent, config);
	}

	launchSuccessModal(res) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = 'success';
		config.nzComponentParams.callback = () => this.loginHelper.processSessionUser(res, this.deviceHelper.getDeviceInformation());
		config.nzComponentParams.message = 'Device was successfully registered';
		this.modal.alert(config);
	}

	back() {
		this.currentView = null;
	}

	async buildLoginRequest(request: LoginRequestModel) {
		this.deviceService
		const deviceInformation = this.getDeviceInformation();
		this.updateRequestIdWithDeviceInformation(request, deviceInformation);
		request.loginType = this.currentStep == this.loginStep?.PASSWORD ? 'PASSWORD' : 'PASSCODE';
		return request;
	}

	async buildLoginV2Request(request: LoginV2RequestModel) {
		const deviceInformation = this.getDeviceInformation();
		this.updateRequestIdWithDeviceInformationV2(request, deviceInformation);
		request.loginType = this.currentStep == this.loginStep?.PASSWORD ? 'PASSWORD' : 'PASSCODE';
		return request;
	}

	updateRequestIdWithDeviceInformation(request: LoginRequestModel, deviceInformation: DeviceInformation) {
		request.deviceUniqueIdentifier = deviceInformation?.deviceUniqueIdentifier;
		request.manufacturer = deviceInformation?.manufacturer;
		request.model = deviceInformation?.model;
		request.loginType = this.currentStep == this.loginStep?.PASSWORD ? 'PASSWORD' : 'PASSCODE';
	}

	updateRequestIdWithDeviceInformationV2(request: LoginV2RequestModel, deviceInformation: DeviceInformation) {
		request.deviceUniqueIdentifier = deviceInformation?.deviceUniqueIdentifier;
		request.manufacturer = deviceInformation?.manufacturer;
		request.model = deviceInformation?.model;
		request.loginType = this.currentStep == this.loginStep?.PASSWORD ? 'PASSWORD' : 'PASSCODE';
	}

	getDeviceInformation() {
		return this.deviceHelper.getDeviceInformation();
	}

	storeUserLoginCredentials(username, password, loginType) {
		this.user.setLoginCredentials({ username, password, loginType });
	}

	handleLoginRequestFailure(err, username) {
		const isNetworkError = !window?.navigator?.onLine;
		isNetworkError ? this.handleNetworkError(username)
			: this.handleNonNetworkError(err, username);
	}

	isNewDeviceDetected(err) {
		const isTwofactorAuthentication = err?.error?.responseCode == ErrorCode.TWO_FACTOR_AUTHENTICATION;
		return err && (
		err?.error?.code === ErrorCode.DEVICE_REGISTRATION ||
		err?.error?.code === ErrorCode.LIVELINESS_REQUIRED ||
		isTwofactorAuthentication
		);
	}

	isAMLFlowDetected(err) {
		return err?.error?.responseCode === ErrorCode.EXTRA_LIVENESS_CHECK 
		|| err?.error?.responseCode === ErrorCode.EXTRA_LIVENESS_OTP_CHECK
	}


	async handleNonNetworkError(err, username) {
		if (this.loginHelper.isStaffUserRefreshTokenEnabled() && err?.error?.code === ErrorCode.ALL_BUSINESSES_CLOSED) {
			this.openAccountsClosedModal();
			return;
		}

		const isAMLVerificationFlowDetected = this.isAMLFlowDetected(err);
		if(isAMLVerificationFlowDetected) {
			this.otpResponse = err?.error;
			this.loginHelper.setLoginOtpResponse(this.otpResponse);
			this.router.navigateByUrl('/login/liveness-check');
			return;
		}
		
		await this.loginHelper.saveClusterManagerUserTokenData(
			(this.clusterManagerService.isClusterManagerUser(err) ? err?.error.tokenDataDeviceResponse : err?.error),
			err?.error?.code === ErrorCode.CLUSTER_MANAGER_USER_LOGIN
		);
		if (err?.error?.code === ErrorCode.CLUSTER_MANAGER_USER_LOGIN) {
			return;
		}

		await this.loginHelper.saveStaffUserTokenData((err?.error?.code === ErrorCode.STAFF_USER_LOGIN ? err?.error.tokenDataDeviceResponse : err?.error), err?.error?.code === ErrorCode.STAFF_USER_LOGIN);
		if (this.loginHelper.isStaffUserRefreshTokenEnabled() &&
			err?.error?.code === ErrorCode.STAFF_USER_LOGIN) {
			return;
		}

		const isNewDeviceDetected = this.isNewDeviceDetected(err);
		isNewDeviceDetected ? this.registerNewDevice(err)
			: this.handleNonDeviceRegistrationError(err, username);
	}

	openAccountsClosedModal() {
		const config = new ModalOptionsConfig();
		config.nzMaskClosable = false;
		this.runningModal?.destroy();
		this.modalService.createModal(AccountsClosedModalComponent, config);
	}

	handleNonDeviceRegistrationError(err, username) {
		const isOtpLockout = err && (err?.error?.code === ErrorCode.OTP_SUSPENSION_V2 || err?.error?.code === ErrorCode.OTP_SUSPENSION);
		const params = { username: username, err };
		isOtpLockout ? this.otpLockout(err) : this.loginFailed(params);
		this.submitting = false;
	}

	async login(request: LoginRequestModel) {
		this.submitting = true;
		request = await this.buildLoginRequest(request);
		this.storeUserLoginCredentials(request?.username, this.currentStep == this.loginStep?.PASSWORD ? request?.password : request?.passcode, this.currentStep == this.loginStep?.PASSWORD ? 'PASSWORD' : 'PASSCODE');
		const response = await this.auth.loginFD(request);
		await this.handleLoginResponse(response, request?.username);
	}

	async loginV2(request: LoginV2RequestModel) {
		this.submitting = true;
		request = await this.buildLoginV2Request(request);
		this.storeUserLoginCredentials(request?.phoneNumber, this.currentStep == this.loginStep?.PASSWORD ? request?.password : request?.passcode, this.currentStep == this.loginStep?.PASSWORD ? 'PASSWORD' : 'PASSCODE');
		const response = await this.auth.loginV2FD(request);
		await this.handleLoginResponse(response, request?.phoneNumber);
	}

	async handleLoginResponse(res, username) {
		this.handleLoginResponseEvent(res, username);
		const deviceInformation = this.getDeviceInformation();
		await this.loginHelper.processSessionUser(res, deviceInformation);
	}

	handleLoginResponseEvent(res, username) {
		const payload = this.getDataLayer(EventName.sign_in_success, username, "", res);
		const payload1 = this.getDataLayer(EventName.identify_user, username, "", res);
		this.dataLayerHelper.logEvent(payload);
		this.dataLayerHelper.logEvent(payload1);
	}

	handleNetworkError(username) {
		const description = this.NETWORK_ERROR;
		const params = { description, username };
		this.networkErrorEvent(params);
		const modalOptions: ModalOptions = this.getNetworkErrorParams();
		this.modalHelper.alert(modalOptions);
	}

	getNetworkErrorParams(): ModalOptions {
		return {
			title: 'No Connectivity',
			description: this.NETWORK_ERROR,
			alertType: 'error',
			callbackName: 'Close',
		};
	}

	networkErrorEvent(params: any) {
		const payload = this.getDataLayer('sign_in_failed', params?.username, params?.description);
		payload[`api_error_message`] = params?.description;
		payload[`api_error_code`] = '';
		this.dataLayerHelper.logEvent(payload);
	}

	registerNewDevice(params) {
		this.otpResponse = params?.error;
		this.identifier = params?.error?.identifier;
		this.tokenData = params?.error?.tokenData;
		this.ussdCode = this.otpResponse?.ussdCode;
		const deviceRegistrationParameters = this.getDeviceRegistrationParameters();
		this.launchMessageModal(deviceRegistrationParameters);
	}

	getDeviceRegistrationParameters() {
		return {
			title: 'New Device Detected',
			description: 'Your login was successful but you need to register this device.',
			messageType: MessageType.WARNING,
			useFullButton: true,
			forNewDevice: true,
			buttonText: 'Register Device'
		}
	}

	loginFailedEvent(params) {
		const description = this.getFailedLoginEventDescription(params);
		const payload = this.getDataLayer('sign_in_failed', params?.username, description);
		payload[`api_error_message`] = StringHelper.getMixpanelErrorMessage(params?.err);
		payload[`api_error_code`] = params?.err?.status;
		this.dataLayerHelper.logEvent(payload);
	}

	getFailedLoginEventDescription(params) {
		return params?.err?.error?.responseMessage ||
			params?.err?.error?.description ||
			StringHelper.getErrorMessageHttpErrorResponse(params?.err);
	}

	loginFailed(params) {
		this.loginFailedEvent(params);
		const modalOptions: ModalOptions = this.getFailedLoginParams(params);
		this.modalHelper.alert(modalOptions);
	}

	getFailedLoginParams(params): ModalOptions {
		return {
			title: 'Login Failed',
			description: params?.err.error?.description || 'An Error occurred',
			alertType: 'error',
			callbackName: 'Close',
		};
	}

	otpLockout(params) {
		const modalOptions: ModalOptions = this.getOtpLockoutParams(params);
		this.modalHelper.alert(modalOptions);
	}

	getOtpLockoutParams(params): ModalOptions {
    const suspensionReleaseTime = params.error?.suspensionReleaseTimeUTC || params.error?.suspensionReleaseTime;
    const suspensionReleaseTimeLocal = new Date(suspensionReleaseTime);
		return {
			title: 'OTP Lockout',
			description: params.error?.responseMessage || params.error?.description || 'An Error occurred',
			alertType: 'error',
			expiryTime: suspensionReleaseTimeLocal,
			callbackName: 'Retry Verification',
		};
	}

	async submitUserNameForm() {
		this.validateFormFields();
		if (this.selectedTabIndex == 0) {
			this.submitPhoneNumberForm();
		} else {
			if (this.userNameForm.invalid) {
				return;
			}
			let request: LoginRequestModel = this.userNameForm.value;
			request = this.checkloginType(request);
			this.login(request).catch(error => { this.handleLoginRequestFailure(error, request?.username); })
		}
	}

	checkloginType(request) {
		if (this.currentStep === this.loginStep.PASSWORD) {
			request.password = this.userNameForm.getRawValue()?.password;
		} else {
			request.passcode = this.userPhoneNumberForm.getRawValue()?.passcode;
		}
		return request;
	}

	async submitPhoneNumberForm() {
		this.validateFormFields();
		if (this.selectedTabIndex == 1) {
			this.submitUserNameForm();
		} else {
			if (this.userPhoneNumberForm.invalid) {
				return;
			}
			let request: LoginV2RequestModel = this.userPhoneNumberForm.value;
			request = this.checkloginType(request);
			this.loginV2(request).catch(error => { this.handleLoginRequestFailure(error, request?.phoneNumber); })
		}
	}

	setOtpForm() {
		this.otpForm = this.fb.group({
			otp: [null, [Validators.required]],
		});
	}

	setLoginForm() {
		// password form
		const credentials = this.user.loginCredentials;
		this.userNameForm = this.fb.group({
			username: [credentials?.username || null],
			password: [null],
		});
		//passcode form
		this.phoneNumber = new PhoneNumberInput(credentials?.phoneNumber || null, 'Phone Number', false);
		this.userPhoneNumberForm = this.fb.group({
			phoneNumber: this.phoneNumber.getFormGroupControl(),
			passcode: [null],
		});
		this.handleLoginFormChange();
		this.setCurrentTab(0)
	}

	handleLoginFormChange() {
		//password form
		this.userNameForm.get('username').valueChanges.subscribe((value) => {
			const username = this.userNameForm?.value?.username;
			!this.isInitiated ? this.logEventOnChangeForm(username) : this.setInitiated(false);
		});

		//passcode form
		this.userPhoneNumberForm.get('phoneNumber').valueChanges.subscribe((value) => {
			const phoneNumber = this.userPhoneNumberForm?.value?.phoneNumber;
			!this.isInitiated ? this.logEventOnChangePhoneNumberForm(phoneNumber) : this.setInitiated(false);
		});
	}

	setInitiated(isInitiated) {
		this.isInitiated = isInitiated;
	}

	logEventOnChangeForm(username) {
		const payload = this.getDataLayer('sign_in_initiated', username);
		this.dataLayerHelper.logEvent(payload);
		this.setInitiated(true);
	}

	logEventOnChangePhoneNumberForm(phoneNumber) {
		const payload = this.getDataLayer('sign_in_initiated', phoneNumber);
		this.dataLayerHelper.logEvent(payload);
		this.setInitiated(true);
	}

	setForms() {
		this.setLoginForm();
		this.setOtpForm();
	}

	getDataLayer(event, username, message = '', data = null) {
		let payload = initializeDatalayer(event);
		payload.error_message = message;
		payload.user_name = data?.tokenData?.username || username;
		payload.email = data?.tokenData?.email || "";
		payload.business_owner_first_name = data?.tokenData?.firstName || "";
		payload.phone_number = data?.tokenData?.phone || "";
		payload.sign_in_mode = 'username and password';
		payload = this.updateDatalayerPayloadIfEventIsIdentifyUser(payload, data, event);
		payload = this.updateDataLayerPayloadIfEventIsSignIn(payload, event);

		return payload;
	}

	updateDatalayerPayloadIfEventIsIdentifyUser(payload: Datalayer, data, event) {
		if (event === 'identify_user') {
			payload = updateDatalayerIdentifyUser(payload);
			payload.email = data?.tokenData?.email;
			payload.business_owner_first_name = data?.tokenData?.firstName;
		}
		return payload
	}

	updateDataLayerPayloadIfEventIsSignIn(payload: Datalayer, event: EventName) {
		if(event === EventName.sign_in_success) {
			this.locationHelper.hasPermission().then( async (hasPermission) => {
				if(hasPermission) {
					const {latitude, longitude} = await this.locationHelper.getCoordinates();
					
					payload.latitude = latitude;
					payload.longitude = longitude;
				}
			});
		}

		return payload;
	}

	signUp() {
		if(this.profileSetupService.useProfileSetup()){
			this.router.navigateByUrl('/onboarding-v2/profile-setup');
			return;
		}
		const canUseNewOnboardingFlow = this.config.USE_NEW_SIGN_UP_FLOW;
		canUseNewOnboardingFlow ? this.handleSignupNavigation()
			: this.router.navigateByUrl('/onboarding/create-account');
	}

	handleSignupNavigation() {
		this.loginHelper.initializeSignup();
		const route = `/onboarding-v2`;
		this.router.navigateByUrl(route);
	}

	private validateFormFields() {
		for (const i in this.userNameForm.controls) {
			if (this.userNameForm && this.userNameForm.controls) {
				this.userNameForm.controls[i].markAsDirty();
				this.userNameForm.controls[i].updateValueAndValidity();
			}
		}

		for (const i in this.userPhoneNumberForm.controls) {
			if (this.userPhoneNumberForm && this.userPhoneNumberForm.controls) {
				this.userPhoneNumberForm.controls[i].markAsDirty();
				this.userPhoneNumberForm.controls[i].updateValueAndValidity();
			}
		}
	}

	moveToPasswordScreen() {
		this.currentStep = this.loginStep.PASSWORD;
		this.userNameForm.get('password').setValidators([Validators.required]);
		this.userNameForm.updateValueAndValidity();
	}

	moveToPasscodeScreen() {
		this.currentStep = this.loginStep.PASSCODE;
		this.userPhoneNumberForm.get('passcode').setValidators([Validators.required, Validators.maxLength(this.MAX_PASSCODE_LENGTH)]);
		this.userPhoneNumberForm.updateValueAndValidity();
	}

	checkUserType(identityType) {
		let value = '';
		if (identityType == 'USERNAME') {
			if (this.userNameForm.invalid) {
				return;
			}
			value = this.userNameForm?.get('username')?.value;
			this.moveToNextStep(identityType, value)
		} else if (identityType == 'PHONENUMBER') {
			if (this.userPhoneNumberForm.invalid) {
				return;
			}
			value = this.userPhoneNumberForm?.get('phoneNumber')?.value;
			this.moveToNextStep(identityType, value);
		}
	}

	moveToNextStep(identityType, value) {
		const params = {
			identityType: identityType,
			value: value
		}
		this.localStorage.saveUserTypePayload(params);
		this.submitting = true;
		this.auth.loginGetUserType(params).then((res) => {
			this.submitting = false;
			if (res?.loginType == 'password') {
				this.moveToPasswordScreen()
			} else {
				this.moveToPasscodeScreen()
			}
		}).catch((error) => {
			this.submitting = false;
			const err = StringHelper.getErrorMessageHttpErrorResponse(error);
			this.errorModal(err);
		});
	}



	setCurrentTab(event) {
		this.selectedTabIndex = event;
		if (this.selectedTabIndex) {
			this.userNameForm.get('username').setValidators([Validators.required]);
			this.userNameForm.updateValueAndValidity();
			this.userPhoneNumberForm.get('phoneNumber').setValidators([])
			this.userPhoneNumberForm.updateValueAndValidity();
		} else {
			this.userNameForm.get('username').setValidators([]);
			this.userNameForm.updateValueAndValidity();
			this.phoneNumber = new PhoneNumberInput(this.userPhoneNumberForm?.get('phoneNumber')?.value || null, 'Phone Number', true);
			this.phoneNumber.setMaxLength(this.MAX_PHONE_LENGTH);
			this.userPhoneNumberForm = this.fb.group({
				phoneNumber: this.phoneNumber.getFormGroupControl(),
				passcode: [null],
			});
			this.userPhoneNumberForm.updateValueAndValidity();
		}
	}

	previousStep() {
		this.currentStep = this.loginStep.LOGIN;
		this.userNameForm.get('password').setValidators([]);
		this.userPhoneNumberForm.get('passcode').setValidators([]);
		this.userNameForm.updateValueAndValidity();
		this.userPhoneNumberForm.updateValueAndValidity();
		this.setCurrentTab(0);

	}

	setPasscode(event) {
		this.userPhoneNumberForm.get('passcode').setValue(event);
	}

	errorModal(message: string) {
		const config = new ModalOptionsConfig()
		config.nzComponentParams.alertType = 'error'
		// config.nzComponentParams.title = '';
		config.nzWidth = 396
		config.nzComponentParams.message = message
		config.nzComponentParams.callback = (result) => { }
		this.runningModal = this.modal.alert(config)
	}

}
